import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useCallback, useMemo } from 'react';
import { useFilterPlacesByLicensedStates } from 'src/views/Coversheet/hooks/useFilterPlacesByLicensedStates';
import { filterPlacesByStates } from 'src/views/Coversheet/utils/filterPlacesByStates';
import { getStateBounds } from 'src/views/Coversheet/utils/googlePlaces';
import { useDebouncedCallback } from 'use-debounce';

let autocompleteService: null | google.maps.places.AutocompleteService = null;

type GooglePlacesPrediction = google.maps.places.AutocompletePrediction & {
  noResults?: boolean;
};

const getGooglePlacesNoResultOption = (
  singularState = false,
): GooglePlacesPrediction => ({
  place_id: 'noResults',
  matched_substrings: [],
  terms: [],
  types: [],
  noResults: true,
  description: 'No Results',
  structured_formatting: {
    main_text_matched_substrings: [],
    main_text: 'No Results',
    secondary_text: singularState
      ? 'Add more details and ensure the address is in the selected state'
      : 'Add more details and ensure the address is in a market you have a license or permissions to transact in',
  },
});

const DEFAULT_RESTRICTIONS: google.maps.places.ComponentRestrictions = {
  country: null,
};

/**
 * A state-scoped search is US-only by definition, and the state's viewport can
 * reach across a border.
 */
const US_RESTRICTIONS: google.maps.places.ComponentRestrictions = {
  country: 'us',
};

type SearchFunction = (
  search: string,
  callback: (options: google.maps.places.AutocompletePrediction[]) => void,
) => void;

const useGooglePlacesSearch = (
  singularState = false,
  limitToLicensedStates = false,
  typesOfPredictionsToUse: string[] | undefined = [],
  restrictions?: google.maps.places.ComponentRestrictions,
  limitToState?: string,
) => {
  const filterPlacesByLicensedStates = useFilterPlacesByLicensedStates();
  const placesLib = useMapsLibrary('places');
  const componentRestrictions =
    restrictions ?? (limitToState ? US_RESTRICTIONS : DEFAULT_RESTRICTIONS);

  const googlePlacesSearch = useCallback(
    async (
      search: string,
      callback: (
        options: google.maps.places.AutocompletePrediction[],
      ) => void | Promise<void>,
    ) => {
      if (!placesLib) return;
      const hasAlphaCharacters = search.length && /[a-zA-Z]+/.test(search);
      const noResults = hasAlphaCharacters
        ? [getGooglePlacesNoResultOption(singularState)]
        : [];
      const locationRestriction = limitToState
        ? await getStateBounds(limitToState)
        : undefined;
      const request: google.maps.places.AutocompletionRequest = {
        input: search,
        types: typesOfPredictionsToUse,
        componentRestrictions,
        locationRestriction,
      };
      autocompleteService ??= new placesLib.AutocompleteService();
      try {
        void autocompleteService.getPlacePredictions(
          request,
          (results: google.maps.places.AutocompletePrediction[] | null) => {
            if (!results) {
              void callback(noResults);
              return;
            }

            const licensedResults = limitToLicensedStates
              ? filterPlacesByLicensedStates(results)
              : results;
            const formattedResults = (
              limitToState
                ? filterPlacesByStates(licensedResults, [limitToState])
                : licensedResults
            ).map((res) => ({
              ...res,
              label: {
                primary: res.structured_formatting.main_text,
                secondary: res.structured_formatting.secondary_text,
              },
            }));
            void callback(
              formattedResults.length > 0 ? formattedResults : noResults,
            );
          },
        );
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: Used for debugging
        console.info('Google Places Search failed', err);
      }
    },
    [
      singularState,
      typesOfPredictionsToUse,
      limitToLicensedStates,
      limitToState,
      filterPlacesByLicensedStates,
      componentRestrictions,
      placesLib,
    ],
  );

  return useDebouncedCallback(googlePlacesSearch, 300);
};

export function useAutofillActionProvider(
  provider?: string,
  providerOptions: {
    singularState?: boolean;
    limitToLicensedStates?: boolean;
    typesOfPredictionsToUse?: string[];
    restrictions?: google.maps.places.ComponentRestrictions;
    limitToState?: string;
  } = {},
) {
  const googlePlacesSearch = useGooglePlacesSearch(
    providerOptions.singularState,
    providerOptions.limitToLicensedStates,
    providerOptions.typesOfPredictionsToUse,
    providerOptions.restrictions,
    providerOptions.limitToState,
  );

  const searchProvider = useMemo(
    () =>
      (
        search: string,
        callback: (
          options: google.maps.places.AutocompletePrediction[],
        ) => void | Promise<void>,
      ) => {
        if (!search || !provider) {
          void callback([]);
          return;
        }
        const queryOrFunction = googlePlacesSearch;
        const searchFunction = queryOrFunction as SearchFunction;

        try {
          if (typeof searchFunction === 'function') {
            googlePlacesSearch(search, callback);
          }
        } catch (error) {
          // biome-ignore lint/suspicious/noConsole: Used for debugging
          console.error('Autofill provider failed', {
            search,
            provider,
            error,
          });
          void callback([]);
        }
      },
    [provider, googlePlacesSearch],
  );

  return searchProvider;
}
